import type {
	IContractDocumentsResponse,
	IFileResponse,
	IResponseBase,
	IUpdateCaseWithProductRequest,
} from '@/interfaces';
import type { DocumentSignRequestPayload } from '@/models/D4Sign';
import { Http } from './http';

const http = Http.create();

export interface DownloadStatus {
	downloadId: string;
	state: number;
	progress: number;
	filePath: string;
	expiresAt: string;
	errorMessage: string | null;
}

export class FileService {
	static async getDocumentById(id: string) {
		const response = await http.get<IResponseBase<IFileResponse>>(`file/${id}`);
		return response.data;
	}

	static async getDocumentSignRequest(id: string) {
		const response = await http.get<IResponseBase<DocumentSignRequestPayload>>(
			`file/${id}/sign-info`,
		);
		return response.data;
	}

	/**
	 * Return a file path if success
	 * @param file
	 * @returns
	 */
	static async uploadFile(file: File, ownerId: string, ownerType: string) {
		const formData = new FormData();
		formData.append('file', file);

		const response = await http.post<string>(`file/${ownerId}/${ownerType}/upload`, formData, {
			headers: { 'Content-Type': 'multipart/form-data' },
		});

		return response.data;
	}

	/**
	 * Return a list of files from a owner
	 * @param ownerId
	 * @param ownerType
	 * @returns
	 */
	static async getOwnerFiles(ownerId: string, ownerType: string) {
		const response = await http.get<IResponseBase<IFileResponse[]>>(`file/${ownerType}/${ownerId}`);

		return response.data;
	}

	/**
	 * Download a file by id
	 * @param fileId
	 * @returns
	 */
	/**
	 * Acervo documental do contrato inteiro numa chamada (ENG-598 §4.2).
	 *
	 * `caseId` é qualquer case da árvore — a raiz ou um descendente; o backend resolve o escopo.
	 * Substitui o leque de `getOwnerFiles` por dono, que dava dez round-trips num contrato com
	 * cinco filhos e quatro inquilinos e ainda deixava o front casar `entityOwnerId` com a lista de
	 * cases na mão.
	 */
	static async getContractDocuments(caseId: string) {
		const response = await http.get<IResponseBase<IContractDocumentsResponse>>(
			`file/contract/${caseId}`,
		);
		return response.data.data;
	}

	/** URL S3 curta, emitida depois de autenticação e policy por tipo de documento. */
	static async getAccessUrl(fileId: string) {
		const response = await http.get<IResponseBase<string>>(`file/${fileId}/access-url`);
		return response.data.data;
	}

	/**
	 * Abre um documento em nova aba pela rota AUTENTICADA.
	 *
	 * Substitui a âncora crua para `/file/storage/{id}/{method}`: âncora não passa pelo
	 * interceptor e portanto não leva o bearer, e desde a ENG-427 aquela rota devolve 401 para
	 * todo arquivo referenciado por um evento de cancelamento (FileController.cs:142-146) — o
	 * Termo de Entrega de Chaves é o caso comum. `access-url` autentica antes e devolve uma URL
	 * Wasabi de 5 minutos.
	 *
	 * Só para "Ver". "Baixar" usa {@link FileService.downloadFile} — PDF/imagem abrem inline
	 * se a URL for só navegada (ENG-611).
	 */
	static async openFile(fileId: string) {
		// A aba nasce ANTES do await: depois dele o clique já saiu do gesto do usuário e o
		// navegador bloqueia o popup (Safari sempre, Chrome com frequência). Sem `noopener`
		// aqui de propósito — com ele window.open devolve null e não sobra handle para navegar.
		const tab = window.open('', '_blank');

		try {
			const url = await FileService.getAccessUrl(fileId);

			if (!tab) {
				// Popup bloqueado mesmo assim: tenta o caminho direto, que o navegador às vezes
				// libera por ser navegação única. Se nem isso passar, avisa — falhar calado aqui
				// devolveria o sintoma que esta função existe para matar ("cliquei e nada").
				if (!window.open(url, '_blank', 'noopener,noreferrer')) {
					window.$message?.error(
						'Não foi possível abrir o documento. Libere os pop-ups para este site.',
					);
				}
				return;
			}

			tab.opener = null;
			tab.location.replace(url);
		} catch (error) {
			tab?.close();
			console.error(error);
			// A policy nega com 404 "File not found" de propósito, para não permitir enumeração
			// (FileAccessPolicy.cs:141). A mensagem não pode dizer que o documento não existe.
			window.$message?.error('Não foi possível abrir o documento.');
		}
	}

	/**
	 * Força o download no disco pela mesma URL autenticada de {@link FileService.openFile}.
	 * Busca o blob e dispara `<a download>` — PDF/imagem não abrem inline (ENG-611).
	 */
	static async downloadFile(fileId: string, fileName?: string) {
		try {
			const url = await FileService.getAccessUrl(fileId);
			const response = await fetch(url);
			if (!response.ok) {
				throw new Error(`Download failed with status ${response.status}`);
			}

			const blob = await response.blob();
			const objectUrl = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = objectUrl;
			link.download = fileName?.trim() || `documento-${fileId}`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(objectUrl);
		} catch (error) {
			console.error(error);
			window.$message?.error('Não foi possível baixar o documento.');
		}
	}

	static deleteFile(fileId: string) {
		return http.delete(`file/${fileId}`);
	}

	/**
	 * Start a batch download of case documents
	 * @param caseId The case ID to download documents from
	 * @returns The download status with ID
	 */
	static async createCaseDocumentsZip(caseId: string) {
		const response = await http.post<DownloadStatus>(`/DocumentDownload/case/${caseId}`);
		return response.data;
	}

	/**
	 * Get the status of a document download
	 * @param downloadId The download ID to check status
	 * @returns The current download status
	 */
	static async getDownloadStatus(downloadId: string) {
		const response = await http.get<DownloadStatus>(`/DocumentDownload/status/${downloadId}`);
		return response.data;
	}

	/**
	 * Download the completed file
	 * @param downloadId The download ID to get the file
	 * @returns The file blob
	 */
	static async downloadZipFile(downloadId: string) {
		const response = await http.get(`/DocumentDownload/file/${downloadId}`, {
			responseType: 'blob',
		});
		return response.data;
	}

	/**
	 * Send file to signature process
	 * @param fileId The file ID to send for signature
	 * @param payload The signature request payload
	 * @returns The response from the signature service
	 */
	static async sendFileToSign(
		fileId: string,
		payload: {
			fileId: string;
			caseId: string;
			workflowId: string;
			activityId: string;
		},
	) {
		const response = await http.post<IResponseBase<any>>(`/file/${fileId}/send-to-sign`, payload);
		return response.data;
	}

	/**
	 * Notify renters about the file to sign
	 * @param fileId The file ID to notify renters about
	 * @returns The response from the notification service
	 */
	static async notifyRenters(fileId: string) {
		const response = await http.post<IResponseBase<any>>(`/file/${fileId}/notify`);
		return response.data;
	}

	/**
	 * Changes the authentication method for a signer
	 * @param fileId
	 * @param signerId
	 * @returns
	 */
	static async updateAuthentication(fileId: string, signerId: string) {
		const response = await http.post<IResponseBase<any>>(
			`/file/${fileId}/signer/${signerId}/update-auth`,
		);
		return response.data;
	}

	/**
	 * Render an HTML fragment into a PDF on the backend.
	 * Backend returns a PDF file stream (blob).
	 */
	static async renderDocument(htmlContent: string) {
		const response = await http.post(
			`file/render-document`,
			{
				content: htmlContent,
				variables: {},
			},
			{
				responseType: 'blob',
				headers: {
					Accept: 'application/pdf',
				},
			},
		);
		return response.data as Blob;
	}

	static async downloadTermsAndConditionDemo(
		caseId: string,
		productId: string,
		request: IUpdateCaseWithProductRequest,
	): Promise<void> {
		const response = await http.post(`file/${caseId}/${productId}/demo`, request, {
			responseType: 'blob',
		});

		const blob = new Blob([response.data], {
			type: response.headers['content-type'] ?? 'application/pdf',
		});

		const url = window.URL.createObjectURL(blob);

		const fileName = `cotacao-${caseId}.pdf`;

		// força download
		const link = document.createElement('a');
		link.href = url;
		link.target = '_blank';
		link.download = fileName;

		document.body.appendChild(link);
		link.click();

		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	}
}
